import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { saleWhatsAppUrl, whatsappRecipient } from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
import WorkModalShell from '@/components/common/WorkModalShell';
import WhatsAppMessagePreview from '@/components/sales/WhatsAppMessagePreview';
import { Select } from '@/components/ui/FormControls';
import { fetchDeliveryShare, deliveryWhatsAppMessage, type DeliveryRow } from '@/lib/deliveries';
import { deliveryButton } from './DeliveryUpdateDialog';

export default function DeliveryWhatsAppDialog({
  rows,
  onClose,
}: {
  rows: DeliveryRow[];
  onClose: () => void;
}) {
  const { organization, profile, access, can } = useAuth();
  const [index, setIndex] = useState(0);
  const [country, setCountry] = useState<'IN' | 'international'>('IN');
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const currentId = rows[index]!.sale_id;
  const alive = useRef(true);
  const busy = useRef(false);
  const operation = useRef<AbortController | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      operation.current?.abort();
    };
  }, []);
  const query = useQuery({
    queryKey: ['delivery_share', organization?.id, profile?.id, access?.access_version, currentId],
    queryFn: ({ signal }) => fetchDeliveryShare(currentId, signal),
    enabled: can('sales.whatsapp'),
    retry: false,
    refetchOnWindowFocus: true,
  });
  const payload = query.data;
  const message = payload ? deliveryWhatsAppMessage(payload) : '';
  const recipient = whatsappRecipient(payload?.customer_mobile || '', country);
  useEffect(() => {
    setLink(null);
  }, [currentId, payload]);
  useEffect(() => {
    setError('');
  }, [currentId]);
  async function openChat() {
    if (!payload || !recipient.digits || busy.current || !can('sales.whatsapp')) return;
    busy.current = true;
    setOpening(true);
    setError('');
    setLink(null);
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const controller = new AbortController();
      operation.current = controller;
      const latest = await fetchDeliveryShare(currentId, controller.signal);
      if (!alive.current) {
        tab?.close();
        return;
      }
      if (JSON.stringify(latest) !== JSON.stringify(payload)) {
        tab?.close();
        await query.refetch();
        setError(
          'The status, tracking or recipient changed. Review the refreshed message and open again.',
        );
        return;
      }
      const mobile =
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const url = saleWhatsAppUrl(
        recipient.digits,
        deliveryWhatsAppMessage(latest),
        mobile ? 'universal' : 'web',
      );
      if (tab) tab.location.replace(url);
      else {
        setLink(url);
        setError('Your browser blocked the new tab. Use the verified link below.');
      }
    } catch (e) {
      tab?.close();
      if (alive.current)
        setError(e instanceof Error ? e.message : 'WhatsApp could not be prepared.');
    } finally {
      busy.current = false;
      operation.current = null;
      if (alive.current) setOpening(false);
    }
  }
  return (
    <WorkModalShell open title="Delivery WhatsApp update" busy={opening} onClose={onClose}>
      <header className="border-b border-border py-3">
        <h2 className="text-lg font-semibold">Delivery update · {rows[index]!.sale_number}</h2>
        <p className="text-sm text-muted-foreground">
          {index + 1} of {rows.length} · Review each customer separately.
        </p>
      </header>
      <div className="space-y-4 overflow-y-auto py-4">
        {query.isPending && <p role="status">Loading current delivery…</p>}
        {query.error && (
          <p role="alert" className="text-sm text-destructive">
            {query.error.message}
          </p>
        )}
        {payload && (
          <>
            <p className="text-sm">
              To: <strong>{payload.customer_name || 'Customer'}</strong> ·{' '}
              {payload.customer_mobile || 'No mobile number'}
            </p>
            <label className="block text-sm">
              Phone format
              <Select
                value={country}
                onChange={(e) => setCountry(e.target.value as 'IN' | 'international')}
              >
                <option value="IN">India (+91)</option>
                <option value="international">International (+country code)</option>
              </Select>
            </label>
            <WhatsAppMessagePreview message={message} />
          </>
        )}
        {recipient.error && payload && (
          <p className="text-sm text-amber-600">
            {recipient.error} You can update customer details in the Sale.
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="block min-h-11 text-primary underline"
            onClick={() => setLink(null)}
          >
            Open verified WhatsApp link
          </a>
        )}
        <p className="text-xs text-muted-foreground">
          You send the message inside WhatsApp. Opening a chat does not confirm delivery of a
          message.
        </p>
      </div>
      <footer className="flex flex-wrap justify-end gap-2 border-t border-border pt-3">
        <button className={deliveryButton} disabled={opening} onClick={onClose}>
          Close
        </button>
        {rows.length > 1 && (
          <>
            <button
              className={deliveryButton}
              disabled={opening || index === 0}
              onClick={() => setIndex(index - 1)}
            >
              Previous
            </button>
            <button
              className={deliveryButton}
              disabled={opening || index === rows.length - 1}
              onClick={() => setIndex(index + 1)}
            >
              Next customer
            </button>
          </>
        )}
        <button
          className={`${deliveryButton} bg-emerald-600 text-white`}
          disabled={
            opening || query.isFetching || !recipient.digits || !payload || !can('sales.whatsapp')
          }
          onClick={() => void openChat()}
        >
          {opening ? 'Checking…' : 'Open WhatsApp'}
        </button>
      </footer>
    </WorkModalShell>
  );
}
